import { ExternalLink, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Locale, PublicPost } from "@vzlive/shared";
import { embedProviderDisplayName, extractEmbedHeight } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";

const CONSENT_KEY = "vzlive:external-media-consent";
// Synchronisiert alle Consent-Gates derselben Seite: „Immer erlauben“
// lädt direkt alle sichtbaren Embeds.
const CONSENT_EVENT = "vzlive:consent-granted";
// Provider, deren Embed-Seiten die echte Inhaltshöhe per postMessage
// liefern – ausgenutzt für Auto-Height ohne Scrollbalken.
const RESIZE_ORIGINS = [
  "https://www.instagram.com",
  "https://platform.twitter.com",
  "https://platform.x.com",
];
const EMBED_MAX_HEIGHT = 2800;

export function ConsentEmbed({
  embed,
  locale,
}: {
  embed: NonNullable<PublicPost["embed"]>;
  locale: Locale;
}) {
  const [allowed, setAllowed] = useState(() => {
    try {
      return localStorage.getItem(CONSENT_KEY) === "granted";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const sync = () => setAllowed(true);
    window.addEventListener(CONSENT_EVENT, sync);
    const storage = (event: StorageEvent) => {
      if (event.key === CONSENT_KEY && event.newValue === "granted") sync();
    };
    window.addEventListener("storage", storage);
    return () => {
      window.removeEventListener(CONSENT_EVENT, sync);
      window.removeEventListener("storage", storage);
    };
  }, []);

  const frameRef = useRef<HTMLDivElement | null>(null);

  // Auto-Height: Instagram/Twitter senden ihre echte Inhaltshöhe per
  // postMessage; nur Nachrichten des eigenen Frames verarbeiten.
  useEffect(() => {
    if (!allowed || !embed.html) return;
    const onMessage = (event: MessageEvent) => {
      if (!RESIZE_ORIGINS.includes(event.origin)) return;
      const iframe = frameRef.current?.querySelector("iframe");
      if (!iframe || event.source !== iframe.contentWindow) return;
      const height = extractEmbedHeight(event.data);
      if (height && height > 60) {
        iframe.style.height = `${Math.min(Math.ceil(height), EMBED_MAX_HEIGHT)}px`;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [allowed, embed.html]);

  const allow = (persist: boolean) => {
    if (persist) {
      try {
        localStorage.setItem(CONSENT_KEY, "granted");
      } catch {
        /* Third-party embeds may have storage disabled; allow this view only. */
      }
    }
    window.dispatchEvent(new Event(CONSENT_EVENT));
    setAllowed(true);
  };

  const description = t(locale, "externalMediaDescription").replace(
    "{provider}",
    embedProviderDisplayName(embed.provider),
  );

  if (allowed && embed.html) {
    return (
      <div
        ref={frameRef}
        className={`embed-frame embed-frame--${embed.provider}`}
        dangerouslySetInnerHTML={{ __html: embed.html }}
      />
    );
  }

  return (
    <div
      className="consent-card"
      style={
        embed.thumbnailUrl
          ? {
              backgroundImage: `linear-gradient(rgba(12,12,12,.74), rgba(12,12,12,.88)), url("${embed.thumbnailUrl}")`,
            }
          : undefined
      }
    >
      <span className="consent-card__icon" aria-hidden="true">
        <Play size={22} />
      </span>
      <div>
        <strong>{embed.title || t(locale, "externalMedia")}</strong>
        <p>{description}</p>
      </div>
      <div className="consent-card__actions">
        {embed.html && (
          <>
            <button type="button" onClick={() => allow(false)}>
              {t(locale, "allowOnce")}
            </button>
            <button type="button" onClick={() => allow(true)}>
              {t(locale, "allowAll")}
            </button>
          </>
        )}
        <a href={embed.url} target="_blank" rel="noopener noreferrer">
          {t(locale, "openFallback")}{" "}
          <ExternalLink size={18} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}
